import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ModuleRegistry,
  AllCommunityModule,
  IRowNode,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { MdSave, MdDelete, MdLock } from "react-icons/md";
import { getAuthorizationHeaders, useAuthenticationService } from "@/services/authentication/authentication-service";
import { SaveModal } from "./SaveModal";
import { DeleteModal } from "./DeleteModal";
import { useVariable } from "@/services/backstage/BackstageProvider";
import { createSuccessToast, createErrorToast } from "@/services/notification/ToastService";
import strings from "./tenantManagement.translations";
import { useTenantSettingsBar } from "../tenantSettings/TenantSettingsBarContext";
import { useStartTenantAdminDelegation } from "./useStartTenantAdminDelegation";
import { guardOnce } from "@/helpers/Utility";

ModuleRegistry.registerModules([AllCommunityModule]);

// =======================================
// Grid layout constants
// =======================================
const ROW_HEIGHT = 42;
const HEADER_HEIGHT = 42;
const MAX_VISIBLE_ROWS = 5;
const GRID_HEIGHT = HEADER_HEIGHT + ROW_HEIGHT * MAX_VISIBLE_ROWS;

// =======================================
// Locked Tenant IDs
// =======================================
const lockedTenantIds = ["22af4c1f-f913-466b-a709-9448792c0c89"];

// =======================================
// UI model
// =======================================
interface TenantInfo {
  id: string; // "" = new row
  name: string;
  databaseName: string;
  isActive: boolean;
}

// =======================================
// API models
// =======================================
interface TenantInfoSM {
  id: string;
  tenantName: string;
  databaseName: string;
  isActive: boolean;
}

interface CreateTenantSM {
  name: string;
  databaseName: string;
  isActive: boolean;
  tenantType: number;
}

interface UpdateTenantSM {
  isActive: boolean;
}

// =======================================
// Mapping helpers
// =======================================
const fromSM = (sm: TenantInfoSM): TenantInfo => ({
  id: sm.id,
  name: sm.tenantName,
  databaseName: sm.databaseName,
  isActive: sm.isActive,
});

const toCreateSM = (ui: TenantInfo): CreateTenantSM => ({
  name: ui.name,
  databaseName: ui.databaseName,
  isActive: ui.isActive,
  tenantType: 3,
});

const toUpdateSM = (ui: TenantInfo): UpdateTenantSM => ({
  isActive: ui.isActive,
});

// =======================================
// TenantGrid component
// =======================================
export const TenantGrid: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useAuthenticationService((s) => [s.user]);
  const startTenantAdminDelegation = useStartTenantAdminDelegation();
  const [rowData, setRowData] = useState<TenantInfo[]>([]);
  const [serverDataMap, setServerDataMap] = useState<Map<string, TenantInfo>>(new Map());
  const gridRef = useRef<AgGridReact>(null);
  const tenantManagementUrl = useVariable("tenantManagementUrl");
  const { open } = useTenantSettingsBar();

  // Synchronous guards (checked/set before any `await`) so a second click on the same
  // row's save/delete/delegate action can't fire the request again while one is in flight.
  const inFlightSavesRef = useRef<Set<TenantInfo>>(new Set());
  const inFlightDeletesRef = useRef<Set<TenantInfo>>(new Set());
  const inFlightDelegationsRef = useRef<Set<string>>(new Set());

  // =======================================
  // Modal state
  // =======================================
  const [saveModalState, setSaveModalState] = useState<{
    visible: boolean;
    row?: TenantInfo;
  }>({ visible: false });

  const [deleteModalState, setDeleteModalState] = useState<{
    visible: boolean;
    row?: TenantInfo;
  }>({ visible: false });

  // =======================================
  // Load tenants from server
  // =======================================
  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${tenantManagementUrl}/tenant/GetTenants`, {
          headers: getAuthorizationHeaders({ includeContentType: false }),
        });
        if (!res.ok) throw new Error(res.statusText);
        const list: TenantInfoSM[] = await res.json();
        const tenants = list.map(fromSM);
        setRowData(tenants);
        setServerDataMap(new Map(tenants.map((t) => [t.id, { ...t }])));
      } catch (e) {
        console.error(e);
        createErrorToast(strings.apiResults.loadError);
      }
    };
    load();
  }, [user, tenantManagementUrl]);

  // =======================================
  // Check if row is dirty
  // =======================================
  const isDirty = (row: TenantInfo): boolean => {
    if (!row.name?.trim() || !row.databaseName?.trim()) return false;
    if (!row.id) return true; // New row
    if (lockedTenantIds.includes(row.id)) return false;
    const server = serverDataMap.get(row.id);
    if (!server) return true;
    return row.isActive !== server.isActive;
  };

  // =======================================
  // Handle cell edit
  // =======================================
  const handleCellEdit = (
    node: IRowNode,
    field: keyof TenantInfo,
    oldValue: any,
    newValue: any
  ) => {
    if (oldValue === newValue || node.rowIndex == null) return;
    setRowData((prev) => {
      const next = [...prev];
      next[node.rowIndex!] = { ...next[node.rowIndex!], [field]: newValue };
      return next;
    });
  };

  // =======================================
  // Save row
  // =======================================
  const saveRow = (row: TenantInfo) => {
    if (!row) return;
    return guardOnce(inFlightSavesRef.current, row, async () => {
      try {
        if (!row.id) {
          // CREATE
          const res = await fetch(`${tenantManagementUrl}/tenant/CreateTenant`, {
            method: "POST",
            headers: getAuthorizationHeaders(),
            body: JSON.stringify(toCreateSM(row)),
          });
          if (!res.ok) throw new Error("Create failed");
          const created: TenantInfoSM = await res.json();
          const newRow = fromSM(created);
          setRowData((prev) => prev.map((r) => (r === row ? newRow : r)));
          setServerDataMap((prev) => new Map(prev).set(newRow.id, { ...newRow }));
          createSuccessToast(strings.apiResults.createSuccess(newRow.name));
        } else {
          // UPDATE
          const res = await fetch(`${tenantManagementUrl}/tenant/UpdateTenant/${row.id}`, {
            method: "PATCH",
            headers: getAuthorizationHeaders(),
            body: JSON.stringify(toUpdateSM(row)),
          });
          if (!res.ok) throw new Error("Update failed");
          setServerDataMap((prev) => new Map(prev).set(row.id, { ...row }));
          createSuccessToast(strings.apiResults.updateSuccess(row.name));
        }
      } catch (e) {
        console.error(e);
        createErrorToast(strings.apiResults.saveError(row.name));
      }
    });
  };

  // =======================================
  // Delete row
  // =======================================
  const deleteRow = (row: TenantInfo) => {
    if (!row) return;
    return guardOnce(inFlightDeletesRef.current, row, async () => {
      try {
        if (row.id) {
          const res = await fetch(`${tenantManagementUrl}/tenant/DeleteTenant/${row.id}`, {
            method: "DELETE",
            headers: getAuthorizationHeaders({ includeContentType: false }),
          });
          if (!res.ok) throw new Error("Delete failed");
          setServerDataMap((prev) => {
            const next = new Map(prev);
            next.delete(row.id);
            return next;
          });
          createSuccessToast(strings.apiResults.deleteSuccess(row.name));
        }
        setRowData((prev) => prev.filter((r) => r !== row));
      } catch (e) {
        console.error(e);
        createErrorToast(strings.apiResults.deleteError(row.name));
      }
    });
  };

  // =======================================
  // Add new row
  // =======================================
  const addRow = () => {
    setRowData((prev) => {
      const newRow: TenantInfo = { id: "", name: "", databaseName: "", isActive: true };
      const next = [...prev, newRow];
      setTimeout(() => gridRef.current?.api.ensureIndexVisible(next.length - 1, "bottom"));
      return next;
    });
  };

  // =======================================
  // Column definitions
  // =======================================
  const columns: ColDef[] = [
    {
      headerName: "",
      field: "lock",
      width: 40,
      cellStyle: { padding: 0 },
      cellRenderer: (params: any) =>
        lockedTenantIds.includes(params.data.id) ? (
          <div className="flex items-center justify-center h-full w-full">
            <MdLock size={24} className="text-gray-800" />
          </div>
        ) : null,
      editable: false,
      sortable: false,
      filter: false,
    },
    {
      field: "id",
      headerName: strings.tenantInfoTablesSection.idColumn,
      minWidth: 320,
      editable: false,
    },
    {
      field: "name",
      headerName: strings.tenantInfoTablesSection.nameColumn,
      flex: 1,
      editable: (params) => !params.data.id && !lockedTenantIds.includes(params.data.id),
      cellRenderer: (params: any) =>
        params.value ? (
          params.value
        ) : (
          <span className="text-gray-400">
            {strings.tenantInfoTablesSection.namePlaceholder}
          </span>
        ),
      onCellValueChanged: (e) =>
        e.node && handleCellEdit(e.node, "name", e.oldValue, e.newValue),
    },
    {
      field: "databaseName",
      headerName: strings.tenantInfoTablesSection.databaseNameColumn,
      flex: 1,
      editable: (params) => !params.data.id && !lockedTenantIds.includes(params.data.id),
      cellRenderer: (params: any) =>
        params.value ? (
          params.value
        ) : (
          <span className="text-gray-400">
            {strings.tenantInfoTablesSection.databaseNamePlaceholder}
          </span>
        ),
      onCellValueChanged: (e) =>
        e.node && handleCellEdit(e.node, "databaseName", e.oldValue, e.newValue),
    },
    {
      field: "isActive",
      headerName: strings.tenantInfoTablesSection.isActiveColumn,
      width: 120,
      editable: (params) => !lockedTenantIds.includes(params.data.id),
      cellEditor: "agSelectCellEditor",
      cellEditorParams: { values: [true, false] },
      onCellValueChanged: (e) =>
        e.node && handleCellEdit(e.node, "isActive", e.oldValue, e.newValue),
    },
    {
      headerName: "",
      width: 120,
      cellRenderer: (params: any) => {
        if (lockedTenantIds.includes(params.data.id)) return null;
        const dirty = isDirty(params.data);
        return (
          <div className="flex justify-end items-center h-full gap-1">
            <button
              onClick={() => {
                if (!params.data.id) {
                  setSaveModalState({ visible: true, row: params.data });
                } else {
                  saveRow(params.data);
                }
              }}
              disabled={!dirty}
              className={`p-1 rounded ${
                dirty
                  ? "text-blue-600 hover:bg-blue-100"
                  : "text-gray-400 cursor-not-allowed"
              }`}
            >
              <MdSave size={24} />
            </button>
            <button
              onClick={() => {
                if (!params.data.id) {
                  setRowData((prev) => prev.filter((r) => r !== params.data));
                } else {
                  setDeleteModalState({ visible: true, row: params.data });
                }
              }}
              className="p-1 rounded text-red-600 hover:bg-red-100"
            >
              <MdDelete size={24} />
            </button>
          </div>
        );
      },
    },
    {
      headerName: strings.tenantInfoTablesSection.delegationColumn,
      width: 290,
      cellRenderer: (params: any) => {
        const row = params.data;

        const isAlreadyDelegating =
          user?.delegation?.isDelegating &&
          user.delegation.delegatedTenantId === row.id;

        const start = () =>
          guardOnce(inFlightDelegationsRef.current, row.id, async () => {
            try {
              await startTenantAdminDelegation(row.id, row.name);
              navigate("/admin-tenant-settings", { replace: true });
            } catch (error) {
              console.error(error);
              createErrorToast(strings.delegationBar.startError);
            }
          });

        const startTenantSettings = () => {
          open(row.id, row.name);
          navigate("/superadmin-tenant-settings", { 
            replace: true,
            state: { tenantId: row.id, name: row.name }
           });
        };

        const baseButton =
          `
          px-2 py-1 h-[32px] rounded
          flex items-center whitespace-nowrap text-sm
          border border-gray-400 dark:border-gray-500
          text-gray-800 dark:text-gray-100
          bg-gray-50 dark:bg-gray-900
          hover:bg-gray-100 dark:hover:bg-gray-800
          active:bg-gray-200 dark:active:bg-gray-700
          transition-colors
          focus:outline-none focus:ring-2 focus:ring-gray-400
          ring-offset-2 ring-offset-white dark:ring-offset-black
          `;

        return (
          <div className="h-full flex items-center gap-2 justify-start w-full">
            <button
              onClick={start}
              disabled={isAlreadyDelegating}
              className={`${baseButton} ${
                isAlreadyDelegating
                  ? "opacity-50 cursor-not-allowed"
                  : "cursor-pointer"
              }`}
            >
              {strings.tenantInfoTablesSection.enterDelegation}
            </button>

            <button
              onClick={startTenantSettings}
              className={`${baseButton} cursor-pointer`}
            >
              {strings.tenantInfoTablesSection.enterSettings}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div style={{ width: "100%" }}>
      {/* Title */}
      <h2 className="text-lg font-semibold mb-3">
        {strings.tenantInfoTablesSection.title}
      </h2>

      <div style={{ marginBottom: 8 }}>
        <button
          onClick={addRow}
          className="px-3 py-1 bg-primary-500 text-white rounded hover:bg-primary-600"
        >
          {strings.tenantInfoTablesSection.addButton}
        </button>
      </div>

      <div
        className="ag-theme-alpine ag-layout-normal"
        style={{
          height: GRID_HEIGHT,
          width: "100%",
          minHeight: GRID_HEIGHT,
          maxHeight: GRID_HEIGHT,
        }}
      >
        <AgGridReact
          ref={gridRef}
          domLayout="normal"
          rowData={rowData}
          columnDefs={columns}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          animateRows={false}
          suppressScrollOnNewData
          stopEditingWhenCellsLoseFocus
          defaultColDef={{ resizable: false }}
        />
      </div>

      {/* Save modal (Create only) */}
      {saveModalState.visible && saveModalState.row && (
        <SaveModal
          visible={saveModalState.visible}
          tenantName={saveModalState.row.name}
          onConfirm={() => {
            saveRow(saveModalState.row!);
            setSaveModalState({ visible: false });
          }}
          onCancel={() => setSaveModalState({ visible: false })}
        />
      )}

      {/* Delete modal */}
      {deleteModalState.visible && deleteModalState.row && (
        <DeleteModal
          visible={deleteModalState.visible}
          title={strings.deleteModal.title}
          message={strings.deleteModal.message(deleteModalState.row.name)}
          confirmLabel={strings.deleteModal.confirm}
          cancelLabel={strings.deleteModal.cancel}
          onConfirm={() => {
            deleteRow(deleteModalState.row!);
            setDeleteModalState({ visible: false });
          }}
          onCancel={() => setDeleteModalState({ visible: false })}
        />
      )}
    </div>
  );
};
